import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { capitalizeAll, ITEMS_PER_PAGE_10, ITEMS_PER_PAGE_LIST, GenericMessageBox, PaginationBar, PAGING_MIN_PAGES, useSearchParams }
    from '@moderepo/mode-app-components';
import { fetchMetricsReportSubscriptions, ritzDataContext, selectMetricsReportSubscriptions, selectProjectById }
    from '@moderepo/mode-data-state-ritz';
import { MetricsReportSubscription } from '@moderepo/mode-internal-apis';
import { selectEntityById, userAppDataContext } from '@moderepo/mode-data-state-user-app';
import { useRitzTranslation } from '../../utils/customHooks';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';
import { EntitySubscriptionsTable, EntitySubscriptionsTableDataItem, EntitySubscriptionsTableProps } from '../../components';
import { getMultipleEntities } from '.';



/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'true'
 */
export interface EntitySubscriptionTableHiddenFields {
    readonly id?: boolean;
    readonly agentId?: boolean;
    readonly entityName?: boolean;
    readonly receiveDailyReport?: boolean;
    readonly receiveWeeklyReport?: boolean;
    readonly receiveMonthlyReport?: boolean;
    readonly notifyOnlyOnAlert?: boolean;
}



export interface EntitySubscriptionsListingContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly title?: string;
    readonly itemsPerPage?: number;
    readonly hiddenFields?: EntitySubscriptionTableHiddenFields;

    // OPTIONAL subscription selected handler. The parent container can implement this handler and do what it needs to do when a sub is selected
    readonly onSubscriptionSelectedHandler?: (sub: MetricsReportSubscription)=> void;
}



/**
 * This container display ALL the Project's Entity Subscriptions. This container does not have any UI, it contain only the logic. It will use
 * the EntitySubscriptionsListingComponent to display the data.
 */
export const EntitySubscriptionsListingContainer: React.FC<EntitySubscriptionsListingContainerProps> = (
    props: EntitySubscriptionsListingContainerProps,
) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | undefined>();


    // State for pagination
    const {
        searchParams, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<{}>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : { },
    });


    // The current page of entity subscriptions based on the current filters
    const currentSubscriptions = selectMetricsReportSubscriptions(dataState, {
        projectId : props.projectId,
        pageNumber: searchParams.currentPage,
        pageSize  : searchParams.itemsPerPage,
    });


    /**
     * Fetch subscription base on the current pagination params
     */
    const fetchMoreSubscription = useCallback(async () => {
        dataDispatch(fetchMetricsReportSubscriptions({
            projectId : props.projectId,
            pageNumber: searchParams.currentPage,
            pageSize  : searchParams.itemsPerPage,
        })).catch(() => {
            // ignore error because dataDispatch already handled the error
        });
    }, [dataDispatch, props.projectId, searchParams.currentPage, searchParams.itemsPerPage]);


    /**
     * This use effect is used for fetching more entity subscription when the itemsPerPage or currentPage from the table pagination changed.
     */
    useEffect(() => {
        fetchMoreSubscription();
    }, [fetchMoreSubscription]);


    /**
     * Whenever we fetched a new page of subscriptions, go through the list of subscriptions and compile a list of entityIds we need to fetch
     */
    const entitiesNeedToBeLoaded = useMemo(() => {
        const subscriptions = currentSubscriptions?.items;
        if (subscriptions && subscriptions.length > 0) {
            return subscriptions.map((sub) => {
                return sub.entityId;
            });
        }
        return undefined;
    }, [currentSubscriptions?.items]);



    /**
     * this useEffect is used for automatically call 'getEntities' to fetch entities data when we have a new list of entitiesNeedToBeLoaded
     */
    useEffect(() => {
        if (entitiesNeedToBeLoaded && entitiesNeedToBeLoaded.length > 0) {
            getMultipleEntities(props.projectId, entitiesNeedToBeLoaded, appDataState, appDataDispatch);
        }
    }, [appDataDispatch, appDataState, entitiesNeedToBeLoaded, props.projectId]);


    /**
     * Localize the Subscription data before passing it to the EntitySubscriptionsTable for display
     */
    const localizedSubscriptionData = useMemo((): readonly EntitySubscriptionsTableDataItem[] | undefined => {
        const subscriptions = currentSubscriptions?.items;

        if (subscriptions && subscriptions.length > 0) {
            // For each subscription, create an Object that contains both the subscription object and the localized subscription's attribute
            return subscriptions.map((sub: MetricsReportSubscription): EntitySubscriptionsTableDataItem => {

                // try to get the entity object from the data state. If we have the entity object, we can display better entity name
                const entity = selectEntityById(appDataState, props.projectId, sub.entityId);

                return {
                    actualValue : sub,
                    displayValue: {
                        entityName: entity?.displaySettings.name ?? sub.entityId,
                    },
                    selected: selectedSubscriptionId === sub.id,
                };
            });
        }

        return undefined;
    }, [appDataState, currentSubscriptions, props.projectId, selectedSubscriptionId]);


    // The table settings that we need to pass to the IssueTableComp. We will create and cache it instead of creating it each time we
    // re-render this container.
    const subscriptionTableSettings = useMemo((): EntitySubscriptionsTableProps['fieldsSettings'] => {
        return {
            fields: {
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.id,
                },
                agentId: {
                    dataItemProp: 'agentId',
                    label       : capitalizeAll(trans('label.agent_id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.agentId,
                },
                entityName: {
                    dataItemProp: 'entityName',
                    label       : capitalizeAll(trans('label.name')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.entityName,
                },
                receiveDailyReport: {
                    dataItemProp: 'receiveDailyReport',
                    label       : capitalizeAll(trans('entity.subscription_list.label.daily_report')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.receiveDailyReport,
                },
                receiveWeeklyReport: {
                    dataItemProp: 'receiveWeeklyReport',
                    label       : capitalizeAll(trans('entity.subscription_list.label.weekly_report')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.receiveWeeklyReport,
                },
                receiveMonthlyReport: {
                    dataItemProp: 'receiveMonthlyReport',
                    label       : capitalizeAll(trans('entity.subscription_list.label.monthly_report')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.receiveMonthlyReport,
                },
                notifyOnlyOnAlert: {
                    dataItemProp: 'notifyOnlyOnAlert',
                    label       : capitalizeAll(trans('entity.subscription_list.label.notify_only_on_alert')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.notifyOnlyOnAlert,
                },
            },
        };
    }, [trans, props.hiddenFields]);



    // The table pagination settings that we need to pass to the EntitySubscriptionsTable. We will create and cache it instead of
    // creating it each time we re-render this container.
    const subscriptionsTablePaginationBar = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : currentSubscriptions && currentSubscriptions.range ? currentSubscriptions.range.total : undefined,
                hasMoreItems    : (currentSubscriptions && currentSubscriptions.range
                    ? currentSubscriptions.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (!currentSubscriptions || currentSubscriptions.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('label.item', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, currentSubscriptions, searchParams, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);



    /**
     * On subscription selected
     */
    const onSubscriptionSelected = useCallback((subscription: MetricsReportSubscription) => {
        setSelectedSubscriptionId(subscription.id);
        if (props.onSubscriptionSelectedHandler) {
            props.onSubscriptionSelectedHandler(subscription);
        }
    }, [props]);



    return (
        <>
            {project && (
                <EntitySubscriptionsTable
                    className={clsx(props.className)}
                    listData={localizedSubscriptionData}
                    title={props.title || capitalizeAll(trans('entity.label.subscription', { count: 2 }))}
                    icon="list"
                    onListItemSelected={onSubscriptionSelected}
                    paginationComp={subscriptionsTablePaginationBar}
                    fieldsSettings={subscriptionTableSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('entity.label.subscription', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('entity.label.subscription', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}


            {!project && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('EntitySubscriptionsListingContainer', EntitySubscriptionsListingContainer);
